/**
 * A client's appointments — read, booked, moved, completed and called off.
 *
 * Five calls, all hung off `/api/v1/appointments/`:
 *
 *   `GET  /api/v1/appointments/?client={public_id}`  the list, paged
 *   `GET  /api/v1/appointments/?inquiry={public_id}` the same, for an enquiry
 *   `GET  /api/v1/appointments/?from=&to=`           one day, every client
 *   `GET  /api/v1/appointments/{id}/`                one appointment, whole
 *   `POST /api/v1/appointments/`                     books one
 *   `POST /api/v1/appointments/{id}/reschedule/`     moves one
 *   `POST /api/v1/appointments/{id}/complete/`       the session took place
 *   `POST /api/v1/appointments/{id}/cancel/`         it did not
 *
 * One endpoint serves both lists — by client, or by a window of time — and the
 * reads and writes below them
 * reach an appointment by its own `public_id`, because a booking is a record
 * in its own right once it exists. See `API_ENDPOINTS.appointments`.
 *
 * An appointment is with a client or with an enquiry, and the wire says which
 * in `type`. It is the one field here that is *not* translated: the backend's
 * own two words are kept, down to its spelling of "inquiry" — see
 * `AppointmentSubject`. What the two change is which single id the booking
 * payload carries, and which of the two name fields a row comes back with.
 *
 * Three things the wire says differently from this side, all of them converted
 * here and nowhere else. (A fourth, `meeting_agenda`, is deliberately *not*
 * converted here: the code is what this side stores — see `ClientAppointment.
 * agenda` — and the words are put back where they are drawn, by
 * `agendaLabel`.)
 *
 *   - Where it happens is `ONLINE` or `IN_PERSON`; here it is "Online" or
 *     "Offline". See `toAppointmentType` and `toTypeCode`.
 *   - A length goes up as `duration_minutes` and comes back as an `ends_at`.
 *     See `toDurationMinutes`.
 *   - Moments carry an offset — `2026-09-20T10:00:00+05:30`. The app holds the
 *     plain instant a `Date` gives it; `toOffsetIso` puts the offset back on
 *     for the two payloads that send one.
 */

import { api, API_ENDPOINTS, withValidationDetail } from "../../../lib/api";
import type { PaginatedResponse } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import { byNewest, bySoonest } from "../appointments";
import { isStandingStatus } from "../types/appointments.types";
import type {
  AppointmentAttendee,
  AppointmentResponse,
  AppointmentStatus,
  AppointmentStatusCode,
  AppointmentSubject,
  AppointmentType,
  AppointmentTypeCode,
  CancelAppointmentPayload,
  CompleteAppointmentPayload,
  ClientAppointment,
  CreateAppointmentPayload,
  NewClientAppointment,
  RescheduleAppointment,
  RescheduleAppointmentPayload,
} from "../types/appointments.types";

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/**
 * `IN_PERSON` is the backend's word; this screen has always said "Offline",
 * which is what the design labels the button. Anything unrecognised reads as
 * Offline rather than throwing — a row the app cannot place is still a row the
 * table has to draw, and "Offline" is the reading that promises least (it
 * offers no link to press).
 */
const toAppointmentType = (code: AppointmentTypeCode | string): AppointmentType =>
  code === "ONLINE" ? "Online" : "Offline";

/** The same journey back, for the two payloads that send it. */
const toTypeCode = (type: AppointmentType): AppointmentTypeCode =>
  type === "Online" ? "ONLINE" : "IN_PERSON";

/**
 * Which of the two the appointment is with, out of the record's own `type`.
 *
 * Two readings rather than one, because the field is newer than the records
 * it describes. An explicit `"inquiry"` settles it; so does an embedded
 * `inquiry` object on a response that carries no `type` at all, which is the
 * shape an older endpoint sends. Anything else is a client's — which is what
 * every appointment was before an enquiry could be booked, and the reading
 * that keeps the rest of the app working exactly as it did.
 */
const toSubject = (response: AppointmentResponse): AppointmentSubject =>
  response.type === "inquiry" || (!response.type && !!response.inquiry)
    ? "inquiry"
    : "client";

/**
 * What the backend decided, in this screen's words.
 *
 * An unknown state reads as `Scheduled`, which puts the row in front of
 * somebody to answer rather than filing it as settled. Guessing "Completed"
 * for a state the app has never seen would quietly write a session into a
 * client's history that may never have happened.
 */
const toStatus = (code: string): AppointmentStatus => {
  if (code === "COMPLETED") return "Completed";
  if (code === "CANCELLED") return "Cancelled";
  if (code === "RESCHEDULED") return "Rescheduled";
  return "Scheduled";
};

/**
 * The length, in minutes, out of the two moments the record carries.
 *
 * `duration_minutes` is a thing this side sends and never receives: the
 * backend stores the far end instead. A record with no `ends_at`, or with one
 * that will not parse, comes back as 0 — which `durationLabel` draws as a
 * dash, rather than as a length somebody chose.
 */
const toDurationMinutes = (response: AppointmentResponse): number => {
  if (!response.ends_at) return 0;

  const from = new Date(response.starts_at).getTime();
  const to = new Date(response.ends_at).getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return 0;

  return Math.round((to - from) / 60_000);
};

/**
 * One appointment, out of the record every verb answers with.
 *
 * Every field is defended: a row the backend sent without an `ends_at` or with
 * a type the app has never heard of is still a row the table has to draw, and
 * a dash in a cell is a better answer than a screen that fails to render.
 */
export const toClientAppointment = (
  response: AppointmentResponse
): ClientAppointment => ({
  id: response.public_id,
  subject: toSubject(response),
  startsAt: response.starts_at,
  type: toAppointmentType(response.appointment_type),
  durationMinutes: toDurationMinutes(response),
  // `""` is what the backend sends for "no link", and an empty string would
  // make `joinable` true and draw a link to nowhere.
  meetingUrl: response.meeting_url || null,
  // Same `""`-is-nothing reading, and `undefined` besides while the backend
  // has no such field to send.
  address: response.address || null,
  // Held as the backend sent it — the code, or the label on a row written
  // before the field had choices. `agendaLabel` is what turns either into the
  // words on screen; keeping the stored value here means a row re-sent later
  // goes back up as what it was. `""` is nothing, as everywhere else on this
  // record.
  agenda: response.meeting_agenda || null,
  description: response.description || null,
  status: toStatus(response.status),
  // Only sent on the reads that span clients — the day window below, and the
  // scheduled list My task draws. A row off a client's own list has nobody to
  // name that the screen does not know.
  clientName: response.client?.name || null,
  clientId: response.client?.public_id || null,
  // The other half, and `null` on every row that has a client: the backend
  // sends one of the two and `null` for the other. Read through
  // `attendeeName` wherever only the words are wanted.
  inquiryName: response.inquiry?.name || null,
  inquiryId: response.inquiry?.public_id || null,
  // Both `""`-is-nothing again. A row only ever carries one of them: a
  // session that was completed was never called off, and one called off never
  // came round to be noted.
  completionNote: response.completion_note || null,
  cancelReason: response.cancellation_reason || null,
});

/**
 * A moment as the backend writes one: `2026-09-20T10:00:00+05:30`.
 *
 * `toISOString` would send the same instant as `...Z`, which is a legal offset
 * and would very likely be accepted. This is written the way the API documents
 * it instead — local parts with the reader's own offset on the end — because
 * the appointment is a time in somebody's day rather than an instant, and a
 * payload a person could read back is worth the dozen lines.
 */
const toOffsetIso = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const pad = (part: number) => String(part).padStart(2, "0");

  // `getTimezoneOffset` counts the other way round: minutes to add to local to
  // reach UTC, so east of Greenwich is negative and the sign flips here.
  const offset = -date.getTimezoneOffset();
  const sign = offset < 0 ? "-" : "+";
  const size = Math.abs(offset);

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `${sign}${pad(Math.floor(size / 60))}:${pad(size % 60)}`
  );
};

/**
 * How many rows a page asks for. A hundred, the most the endpoint allows,
 * because the tab wants the whole list at once — it cuts it four ways, pages
 * it and reads a week out of it — so the fewer round trips the walk below
 * takes, the better.
 */
const PAGE_SIZE = 100;

/**
 * Stops a broken `next` from looping forever. At a hundred a page this is
 * 2,000 appointments against one client, which is far past a lifetime of them.
 */
const MAX_PAGES = 20;

/**
 * One day as the list endpoint asks for it: local midnight to local midnight,
 * with the reader's own offset on both ends.
 *
 * Local parts rather than anything off `toISOString`, which is midnight in
 * Greenwich — a practitioner in Kolkata asking for today before half past five
 * in the morning would otherwise be handed yesterday. The window is matched by
 * overlap at the far end, so an appointment that began late last night and
 * runs into this morning comes back on both days, which is correct: it is on
 * both.
 */
const dayWindow = (day: Date): { from: string; to: string } => {
  const from = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const to = new Date(from);
  to.setDate(to.getDate() + 1);

  return {
    from: toOffsetIso(from.toISOString()),
    to: toOffsetIso(to.toISOString()),
  };
};

/**
 * One day off the list endpoint, whole, in the order it will be lived.
 *
 * The raw read the two day-shaped callers below share. Neither filters at the
 * server: `status` would take one constant where both of them want several,
 * and `classification` reads the clock at the moment the request was answered,
 * which is not a thing a screen that stays open all morning can be built on.
 * What comes back is the day, and each caller cuts it for itself.
 *
 * One page of a hundred — the most the endpoint allows. A hundred appointments
 * in a single day is past anything a practice books, and neither caller is a
 * place to walk pages: one is a dialog, the other is a screen that has to
 * redraw every time somebody presses a different day.
 */
const fetchDay = async (
  getToken: GetToken,
  day: Date,
  what: string,
  signal?: AbortSignal
): Promise<ClientAppointment[]> => {
  const response = await api.get<PaginatedResponse<AppointmentResponse>>(
    API_ENDPOINTS.appointments.list,
    {
      token: await token(getToken, what),
      query: { ...dayWindow(day), page_size: PAGE_SIZE },
      signal,
    }
  );

  return (response?.results ?? []).map(toClientAppointment).sort(bySoonest);
};

/**
 * Every appointment standing on one day, across every client.
 *
 * What the Schedule and Reschedule dialogs check a new slot against. Across
 * clients on purpose: the thing that cannot be double-booked is the
 * practitioner, and an hour already promised to one client is not free
 * because the dialog happens to be open on another.
 *
 * The cut is made here rather than by the endpoint's `status`. `SCHEDULED`
 * alone would miss every appointment that has been moved — those come back
 * `RESCHEDULED` and are every bit as booked — so the question is put to
 * `isStandingStatus`, which is the app's one answer to "is this still
 * happening". Cancelled and completed slots are free.
 */
export async function fetchDayAppointments(
  getToken: GetToken,
  day: Date,
  signal?: AbortSignal
): Promise<ClientAppointment[]> {
  const booked = await fetchDay(
    getToken,
    day,
    "check the day for clashes",
    signal
  );

  return booked.filter((appointment) => isStandingStatus(appointment.status));
}

/**
 * The same day, uncut — what My task draws.
 *
 * The sibling of the read above and the opposite of it in one respect: that
 * one throws away everything already settled, and this keeps all of it. A
 * day's diary has to account for the hours that did not go to plan as much as
 * the ones that did — a morning that was cancelled and an afternoon that was
 * completed are both things the screen has to say — so nothing is filtered at
 * either end.
 *
 * Soonest first, because the screen reads down the day. Sorted here rather
 * than trusted to arrive that way: the order is this side's decision, not one
 * to inherit from whatever the endpoint happened to send.
 */
export async function fetchDayDiary(
  getToken: GetToken,
  day: Date,
  signal?: AbortSignal
): Promise<ClientAppointment[]> {
  return fetchDay(getToken, day, "load this day's appointments", signal);
}

/**
 * One appointment, read fresh by its own `public_id`.
 *
 * What the Meeting dialog opens on. The row is already in hand when it opens —
 * it was pressed on a list — so this is not what draws the dialog; it is what
 * keeps it honest. A diary moves while a screen sits open, and everything the
 * dialog offers to do (join it, move it, call it off) turns on the record
 * still standing as the list last saw it.
 */
export async function fetchAppointment(
  getToken: GetToken,
  appointmentId: string,
  signal?: AbortSignal
): Promise<ClientAppointment> {
  return toClientAppointment(
    await api.get<AppointmentResponse>(
      API_ENDPOINTS.appointments.detail(appointmentId),
      { token: await token(getToken, "load this appointment"), signal }
    )
  );
}

/**
 * What the list endpoint can be narrowed by, past the day window the two
 * reads above use.
 *
 * `client` and `inquiry` are the same question asked of the two kinds of
 * attendee, and `type` is that question without the id — every appointment
 * with an enquiry, whoever's. Sending `client` and `inquiry` together would
 * ask for an appointment that is both, which is nothing; the callers below
 * each send one.
 */
export interface AppointmentFilters {
  /** A client's `public_id`. */
  client?: string;
  /** An enquiry's `public_id`. */
  inquiry?: string;
  /** `"client"` or `"inquiry"` — which kind, without naming one. */
  type?: AppointmentSubject;
  /** One of the backend's status codes: `SCHEDULED`, `CANCELLED`, … */
  status?: AppointmentStatusCode;
  /** 1-based, the way the endpoint counts. */
  page?: number;
  pageSize?: number;
}

/**
 * One page of the list, narrowed however the caller asked — in the order the
 * backend sent them.
 *
 * The one place the list's filters are spelled out, rather than a function
 * per question: they are all the same request with a different pair of query
 * keys, and the walk below is the only thing on top of it.
 */
export async function fetchAppointmentsPage(
  getToken: GetToken,
  { client, inquiry, type, status, page, pageSize = PAGE_SIZE }: AppointmentFilters,
  signal?: AbortSignal
): Promise<{ appointments: ClientAppointment[]; hasNext: boolean }> {
  const response = await api.get<PaginatedResponse<AppointmentResponse>>(
    API_ENDPOINTS.appointments.list,
    {
      token: await token(getToken, "load appointments"),
      // `undefined` entries are dropped by the API client, so a filter nobody
      // asked for never reaches the URL.
      query: {
        client,
        inquiry,
        type,
        status,
        page,
        page_size: pageSize,
      },
      signal,
    }
  );

  return {
    appointments: (response?.results ?? []).map(toClientAppointment),
    hasNext: !!response?.next,
  };
}

/**
 * Whether this practice has ever booked an appointment at all.
 *
 * One row asked for and none of it read — only whether anything came back.
 * `page_size=1` because the question is about existence, and a hundred rows
 * cost the same to ignore as one costs to answer with.
 *
 * No filter of any kind, deliberately. Not `status`, which would make a
 * practice whose only two appointments were cancelled look as though it had
 * never booked one; not a date window, which would make the answer depend on
 * when it was asked. "Ever" means ever — a cancelled appointment last March
 * is still a practitioner who knows what this screen is for.
 *
 * What it decides is a layout rather than a list: My task shows its first-run
 * panel on an empty day only while this is false. See `useEverBooked`.
 */
export async function hasEverBooked(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<boolean> {
  const { appointments } = await fetchAppointmentsPage(
    getToken,
    { pageSize: 1 },
    signal
  );

  return appointments.length > 0;
}

/** One page of a client's appointments. The shape the walk below wants. */
export async function fetchClientAppointmentsPage(
  getToken: GetToken,
  clientId: string,
  page?: number,
  signal?: AbortSignal
): Promise<{ appointments: ClientAppointment[]; hasNext: boolean }> {
  return fetchAppointmentsPage(getToken, { client: clientId, page }, signal);
}

/** The same, for an enquiry — `?inquiry=` where that one sends `?client=`. */
export async function fetchEnquiryAppointmentsPage(
  getToken: GetToken,
  inquiryId: string,
  page?: number,
  signal?: AbortSignal
): Promise<{ appointments: ClientAppointment[]; hasNext: boolean }> {
  return fetchAppointmentsPage(getToken, { inquiry: inquiryId, page }, signal);
}

/**
 * A client's whole appointment list, newest first.
 *
 * Every page rather than one of them, and no `classification` or `status`
 * filter, though the endpoint offers both. The screen holds one list and reads
 * four different questions out of it at once: the table cuts it into All,
 * Upcoming, Past and Cancelled, the carousel wants the next seven days, and
 * the strip above the tabs wants today. Asking the server for each would be
 * six requests and six caches that can disagree with one another about what
 * "past" means the moment one of them is a minute staler than the next.
 *
 * It moves to the server the day one client has enough appointments for the
 * walk to be felt — which, for a practice, is a long way off.
 *
 * Sorted here rather than trusted to arrive that way: the table reads top-down
 * as most-recent-first, and that is the app's decision to make rather than one
 * to inherit from whatever order the pages came back in.
 */
export async function fetchClientAppointments(
  getToken: GetToken,
  clientId: string,
  signal?: AbortSignal
): Promise<ClientAppointment[]> {
  const appointments: ClientAppointment[] = [];

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const result = await fetchClientAppointmentsPage(
      getToken,
      clientId,
      page,
      signal
    );
    appointments.push(...result.appointments);

    // An empty page as well as `next: null` ends the walk — a page that says
    // there is another but returns nothing would otherwise spin to the cap.
    if (!result.hasNext || result.appointments.length === 0) break;
  }

  return appointments.sort(byNewest);
}

/**
 * Books one — with a client, or with an enquiry — and answers with the row as
 * the backend stored it: its `public_id` and its `ends_at`, neither of which
 * the caller knew before it asked.
 *
 * Which of the two it is with is the `attendee`, and the only thing it
 * changes on the wire is which single id key the payload carries. Everything
 * else about booking an hour is the same question whoever it is with, which
 * is why this is one function with an attendee rather than two that would
 * drift.
 *
 * A 400 arrives as an `ApiError` whose message names the fields the backend
 * rejected rather than just "Validation failed" — a clash with another
 * appointment is the one this screen will meet most.
 */
export async function createAppointment(
  getToken: GetToken,
  attendee: AppointmentAttendee,
  input: NewClientAppointment,
  signal?: AbortSignal
): Promise<ClientAppointment> {
  const body: CreateAppointmentPayload = {
    // One key or the other, never both and never `null`: which one went up is
    // what the response comes back typed as. See `AppointmentAttendee`.
    ...(attendee.subject === "inquiry"
      ? { inquiry: attendee.id }
      : { client: attendee.id }),
    starts_at: toOffsetIso(input.startsAt),
    duration_minutes: input.durationMinutes,
    appointment_type: toTypeCode(input.type),
    // Left off entirely rather than sent empty: an in-person appointment has
    // nowhere to be joined, and `""` is not a URL the backend could store.
    ...(input.type === "Online" && input.meetingUrl
      ? { meeting_url: input.meetingUrl }
      : {}),
    // And the mirror of it: an address belongs to an in-person sitting, and
    // an online appointment has no room to send.
    ...(input.type === "Offline" && input.address
      ? { address: input.address }
      : {}),
    // Both left off where there is nothing to say, rather than sent as `""`.
    // `meeting_agenda` is a choice field and `""` is not one of its seven; the
    // description follows the same rule so the two halves of one question keep
    // the same shape on the wire.
    ...(input.agenda ? { meeting_agenda: input.agenda } : {}),
    ...(input.description?.trim()
      ? { description: input.description.trim() }
      : {}),
  };

  try {
    return toClientAppointment(
      await api.post<AppointmentResponse>(API_ENDPOINTS.appointments.list, body, {
        token: await token(getToken, "book an appointment"),
        signal,
      })
    );
  } catch (error) {
    throw withValidationDetail(error);
  }
}

/**
 * Moves one to a new slot and a new length, and answers with the whole row as
 * it now stands — so what lands on screen is what was stored rather than what
 * the dialog assumed.
 */
export async function rescheduleClientAppointment(
  getToken: GetToken,
  appointmentId: string,
  input: RescheduleAppointment,
  signal?: AbortSignal
): Promise<ClientAppointment> {
  const body: RescheduleAppointmentPayload = {
    starts_at: toOffsetIso(input.startsAt),
    duration_minutes: input.durationMinutes,
    // `undefined` where the dialog had no address to offer — an online
    // appointment, or an offline one whose room did not move — and `""` where
    // one was cleared, which is a change the backend is meant to store rather
    // than a field to skip. So the test is against `undefined`, not falsiness.
    ...(input.address === undefined || input.address === null
      ? {}
      : { address: input.address }),
  };

  try {
    return toClientAppointment(
      await api.post<AppointmentResponse>(
        API_ENDPOINTS.appointments.reschedule(appointmentId),
        body,
        { token: await token(getToken, "reschedule an appointment"), signal }
      )
    );
  } catch (error) {
    throw withValidationDetail(error);
  }
}

/**
 * Says the session took place, with whatever the practitioner wrote down about
 * it, and answers with the row as the backend now holds it.
 *
 * The note is optional and goes up as `""` where there is none — sent rather
 * than left off, so the field is always the same shape on the wire and a note
 * cleared on a second pass is stored as cleared rather than skipped. Same rule
 * the cancellation's reason follows.
 */
export async function completeClientAppointment(
  getToken: GetToken,
  appointmentId: string,
  note = "",
  signal?: AbortSignal
): Promise<ClientAppointment> {
  const body: CompleteAppointmentPayload = { note: note.trim() };

  try {
    return toClientAppointment(
      await api.post<AppointmentResponse>(
        API_ENDPOINTS.appointments.complete(appointmentId),
        body,
        { token: await token(getToken, "complete an appointment"), signal }
      )
    );
  } catch (error) {
    throw withValidationDetail(error);
  }
}

/**
 * Calls one off, with the reason where one was given, and returns the row as
 * the backend now holds it.
 *
 * Both ways of calling one off come here: the Cancel on an appointment still
 * ahead, and the cross on one whose time has passed without the session
 * happening. The backend keeps one state for an appointment that did not take
 * place however it failed to, so the screen keeps one too — the reason is
 * where the difference between the two is written down.
 */
export async function cancelClientAppointment(
  getToken: GetToken,
  appointmentId: string,
  reason: string,
  signal?: AbortSignal
): Promise<ClientAppointment> {
  const body: CancelAppointmentPayload = { reason: reason.trim() };

  try {
    return toClientAppointment(
      await api.post<AppointmentResponse>(
        API_ENDPOINTS.appointments.cancel(appointmentId),
        body,
        { token: await token(getToken, "cancel an appointment"), signal }
      )
    );
  } catch (error) {
    throw withValidationDetail(error);
  }
}
